import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Power, LogIn, Gauge, Search, Mail, Phone, MapPin, Calendar, Activity,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Label, scoreColor, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { fmtDateTime } from "../../../lib/time";

const SORTS = [
  { v: "recent", label: "Recent activity" },
  { v: "size", label: "Size (employees)" },
  { v: "ratings", label: "Ratings volume" },
  { v: "newest", label: "Newest signup" },
  { v: "name", label: "Name (A→Z)" },
];

export default function PlatformOrganizations() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const canManage = ["master_admin", "platform_ops"].includes(user.role);
  const isMaster = user.role === "master_admin";
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planSel, setPlanSel] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [industry, setIndustry] = useState("all");

  const load = useCallback(() => api.get("/platform/organizations").then((r) => setRows(r.data)).catch((e) => console.warn("Organizations load failed", e)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isMaster) api.get("/platform/plans").then((r) => setPlans(r.data)).catch(() => {}); }, [isMaster]);

  const industries = useMemo(() => Array.from(new Set(rows.map((r) => r.industry).filter(Boolean))).sort(), [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (r) => {
      if (industry !== "all" && r.industry !== industry) return false;
      if (!term) return true;
      return [r.name, r.industry, r.area, r.contact?.email, r.contact?.phone, r.contact?.name]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
    };
    const picked = rows.filter(match);
    const cmp = {
      recent: (a, b) => (b.last_active || "").localeCompare(a.last_active || ""),
      size: (a, b) => (b.employees || 0) - (a.employees || 0),
      ratings: (a, b) => (b.verified_interactions || 0) - (a.verified_interactions || 0),
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    }[sort];
    return [...picked].sort(cmp);
  }, [rows, q, industry, sort]);

  const active = filtered.filter((r) => !r.is_inactive);
  const inactive = filtered.filter((r) => r.is_inactive);

  const open = async (o) => {
    if (!canManage) { setDetail({ metrics: o, organization: o, locations: [], basic: true }); return; }
    try {
      const { data } = await api.get(`/platform/organizations/${o.id}`);
      setDetail(data);
      setPlanSel(data.subscription?.plan_id || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  const changePlan = async () => {
    if (!planSel) return;
    try {
      await api.patch(`/platform/organizations/${detail.organization.id}/subscription`, { plan_id: planSel, status: "active" });
      toast.success("Tier updated");
      const { data } = await api.get(`/platform/organizations/${detail.organization.id}`);
      setDetail(data);
      setPlanSel(data.subscription?.plan_id || "");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const impersonate = async (orgId) => {
    try {
      await api.post(`/platform/impersonate/${orgId}`);
      await refresh();
      toast.success("Entered corporate workspace");
      navigate("/app");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleStatus = async () => {
    const next = detail.organization.status === "suspended" ? "active" : "suspended";
    try {
      await api.patch(`/platform/organizations/${detail.organization.id}/status`, { status: next });
      toast.success(`Organisation ${next}`);
      setDetail({ ...detail, organization: { ...detail.organization, status: next } });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div><Label>Platform</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Organisations</h1></div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid="org-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, industry, city, contact…"
                 className="pl-9" data-testid="org-search" />
        </div>
        <Select value={industry} onValueChange={setIndustry}>
          <SelectTrigger className="w-44" data-testid="org-industry"><SelectValue placeholder="All segments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All segments</SelectItem>
            {industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-48" data-testid="org-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-slate-400" data-testid="org-counts">
          {active.length} active · {inactive.length} inactive
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Building2} title="No organisations match" hint="Try clearing filters or search terms." />
      ) : (
        <>
          <Section title="Active" subtitle="Collected feedback or added staff in the last 30 days" count={active.length}>
            <Grid rows={active} onOpen={open} />
          </Section>
          <Section title="Inactive" subtitle="No feedback or employee activity in the last 30 days — follow up to re-engage" count={inactive.length} muted>
            <Grid rows={inactive} onOpen={open} muted />
          </Section>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>{detail?.organization?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Mini label="Service Index" value={detail.metrics.ssi.toFixed(2)} color={scoreColor(detail.metrics.ssi)} />
                <Mini label="Interactions" value={detail.metrics.verified_interactions} />
                <Mini label="Open recovery" value={detail.metrics.open_recovery} />
              </div>
              {detail.contact && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <Label className="mb-1 block normal-case tracking-normal text-slate-600">Signup contact</Label>
                  {detail.contact.name && <p className="font-semibold text-slate-800">{detail.contact.name}</p>}
                  <div className="flex flex-wrap gap-3 text-xs">
                    {detail.contact.email && <a href={`mailto:${detail.contact.email}`} className="inline-flex items-center gap-1 text-indigo-600 hover:underline" data-testid="detail-email"><Mail className="h-3 w-3" />{detail.contact.email}</a>}
                    {detail.contact.phone && <a href={`tel:${detail.contact.phone}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-indigo-700" data-testid="detail-phone"><Phone className="h-3 w-3" />{detail.contact.phone}</a>}
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    Segment: {detail.organization?.industry || "—"} · Area: {detail.area || "—"} ·
                    Signup {fmtDateTime(detail.organization?.created_at)} ·
                    Last active {detail.last_active ? fmtDateTime(detail.last_active) : "never"}
                  </p>
                </div>
              )}
              {detail.subscription && <p className="rounded-lg bg-slate-50 p-3">Plan: <b>{detail.subscription.plan_name}</b> · R{detail.subscription.mrr}/mo · {detail.subscription.status}</p>}
              {isMaster && plans.length > 0 && !detail.basic && (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="tier-assign">
                  <Label className="mb-1 block normal-case tracking-normal text-slate-600">Change tier</Label>
                  <div className="flex gap-2">
                    <Select value={planSel} onValueChange={setPlanSel}>
                      <SelectTrigger data-testid="tier-select" className="flex-1"><SelectValue placeholder="Choose tier" /></SelectTrigger>
                      <SelectContent>
                        {plans.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} · {p.max_locations == null ? "∞" : p.max_locations} loc / {p.max_employees == null ? "∞" : p.max_employees} emp · R{p.price}/mo
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={changePlan} data-testid="apply-tier"
                      disabled={!planSel || planSel === detail.subscription?.plan_id}
                      className="bg-[#4338CA] hover:bg-[#3730A3]">Apply</Button>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">Switch between Free, Professional, Growth or Enterprise to trial the caps live. MRR defaults to the tier's list price.</p>
                </div>
              )}
              {detail.plan_limits && (
                <div className="grid grid-cols-2 gap-3" data-testid="org-plan-usage">
                  <Usage label="Locations" used={detail.plan_limits.locations_used} max={detail.plan_limits.max_locations} />
                  <Usage label="Employees" used={detail.plan_limits.employees_used} max={detail.plan_limits.max_employees} />
                </div>
              )}
              {!detail.basic && detail.locations?.length > 0 && (
                <div>
                  <Label>Locations</Label>
                  <div className="mt-2 space-y-1">
                    {detail.locations.map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-lg bg-slate-50 p-2">
                        <span className="font-medium text-slate-700">{l.name} <span className="text-slate-400">· {l.city}</span></span>
                        <span className="text-slate-500">SSI {l.ssi.toFixed(1)} · {l.employees} emp · {l.nfc_provisioned} NFC</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isMaster && (
                <Button onClick={() => impersonate(detail.organization.id)} data-testid="impersonate-org"
                  className="w-full bg-[#4338CA] hover:bg-[#3730A3]">
                  <LogIn className="mr-1 h-4 w-4" /> Enter corporate admin workspace
                </Button>
              )}
              {isMaster && (
                <Button onClick={toggleStatus} data-testid="toggle-org-status"
                  className={detail.organization.status === "suspended" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>
                  <Power className="mr-1 h-4 w-4" /> {detail.organization.status === "suspended" ? "Reactivate organisation" : "Suspend organisation"}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, subtitle, count, muted, children }) {
  if (!count) return null;
  return (
    <section>
      <div className="mb-2 flex items-end justify-between">
        <div>
          <h2 className={`font-head text-base font-bold ${muted ? "text-slate-500" : "text-slate-900"}`}>{title}</h2>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
        <span className="text-xs text-slate-400">{count} org{count === 1 ? "" : "s"}</span>
      </div>
      {children}
    </section>
  );
}

function Grid({ rows, onOpen, muted }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((o) => (
        <button key={o.id} onClick={() => onOpen(o)} data-testid={`org-card-${o.id}`}
          className={`rounded-xl border bg-white p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-indigo-300 ${
            muted ? "border-slate-100 opacity-80" : "border-slate-200"
          }`}>
          <div className="flex items-start justify-between">
            <Building2 className={`h-5 w-5 ${muted ? "text-slate-300" : "text-indigo-500"}`} />
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
              o.status === "suspended" ? "bg-red-50 text-red-600" : muted ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"
            }`}>{muted ? "inactive" : o.status}</span>
          </div>
          <p className="mt-2 font-head text-lg font-semibold text-slate-900">{o.name}</p>
          <p className="text-xs text-slate-400">{o.industry || "—"} · {o.plan || "No plan"}</p>

          <div className="mt-3 space-y-1 text-xs text-slate-500">
            {o.area && <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-slate-400" />{o.area}</p>}
            <p className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-slate-400" />Signup {o.created_at ? fmtDateTime(o.created_at) : "—"}</p>
            <p className="flex items-center gap-1.5"><Activity className="h-3 w-3 text-slate-400" />Last active {o.last_active ? fmtDateTime(o.last_active) : <span className="text-slate-300">never</span>}</p>
            {o.contact?.email && (
              <a href={`mailto:${o.contact.email}`} onClick={(e) => e.stopPropagation()}
                 className="inline-flex items-center gap-1.5 text-indigo-600 hover:underline" data-testid={`card-email-${o.id}`}>
                <Mail className="h-3 w-3" />{o.contact.email}
              </a>
            )}
            {o.contact?.phone && (
              <a href={`tel:${o.contact.phone}`} onClick={(e) => e.stopPropagation()}
                 className="ml-3 inline-flex items-center gap-1.5 text-slate-600 hover:text-indigo-700" data-testid={`card-phone-${o.id}`}>
                <Phone className="h-3 w-3" />{o.contact.phone}
              </a>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-slate-500">{o.locations} loc · {o.employees} emp</span>
            <span className="sv-metric font-bold" style={{ color: scoreColor(o.ssi) }}>{o.ssi.toFixed(2)}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function Mini({ label, value, color = "#0F172A" }) {
  return <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400">{label}</p><p className="sv-metric text-xl font-bold" style={{ color }}>{value}</p></div>;
}

function Usage({ label, used, max }) {
  const unlimited = max == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
  const near = !unlimited && used >= max;
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-400"><Gauge className="h-3.5 w-3.5" /> {label}</div>
      <p className="sv-metric mt-1 text-lg font-bold text-slate-800">{used}<span className="text-sm font-medium text-slate-400"> / {unlimited ? "∞" : max}</span></p>
      {!unlimited && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${near ? "bg-red-500" : "bg-[#4338CA]"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
